import { useState, useMemo, useRef, useEffect, useLayoutEffect, useCallback, type RefObject } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import clsx from "clsx";
import toast from "react-hot-toast";
import { useQuery } from "@tanstack/react-query";
import { GuessGrid, ScoreBadge, type GuessCell } from "./guess-grid";
import UserScoreRow, { type UserScoreRowData } from "./user-score-row";
import CommentLikeControl from "./comment-like-control";
import { deletePlayablePropertyComment } from "@/services/PropertyService";
import { getLikeCounts } from "@/services/LikeService";
import { COMMENT_LIKE_TARGET_TYPE } from "@/constants/like.constants";
import { PUZZLE_FOCUS_COMMENT_ID_KEY, PUZZLE_FOCUS_COMMENT_PATH_KEY } from "@/constants/puzzle-storage.constants";
import { findCommentIdsRequiringReplyExpansion, PUZZLE_VISIBLE_REPLY_LIMIT } from "@/helpers/puzzle-comment-focus";
import {
    readPuzzleFocusCommentIdFromSearch,
    stripPuzzleCommentIdFromSearch,
} from "@/helpers/puzzle-comment-url";
import {
    resolveScoreboardScrollContainer,
    scrollElementWithinContainer,
} from "@/helpers/puzzle-comment-scroll";
import moment from "moment";
import useMixpanelEvent from "@/hooks/useMixpanelEvent";
import { useUser } from "@/context/UserContext";
import useAgentFollow from "@/hooks/useAgentFollow";

const FOCUS_COMMENT_SCROLL_RETRY_MS = 400;
const FOCUS_COMMENT_SCROLL_MAX_ATTEMPTS = 8;
const MAX_REPLY_LENGTH = 10000;

function resolveCommentLikeState(
    apiCommentId: string | null | undefined,
    commentLikeStateById: Map<string, { count: number; likedByCurrentUser: boolean }>,
): { commentLikeCount: number; commentLikedByCurrentUser: boolean } {
    if (!apiCommentId) {
        return { commentLikeCount: 0, commentLikedByCurrentUser: false };
    }
    const entry = commentLikeStateById.get(String(apiCommentId));
    return {
        commentLikeCount: entry?.count ?? 0,
        commentLikedByCurrentUser: entry?.likedByCurrentUser ?? false,
    };
}

function collectApiCommentIds(comments: unknown[]): string[] {
    const ids: string[] = [];
    const walk = (list: unknown[]) => {
        for (const node of list) {
            if (!node || typeof node !== "object") {
                continue;
            }
            const record = node as { id?: string | null; replies?: unknown[] };
            if (record.id) {
                ids.push(String(record.id));
            }
            if (Array.isArray(record.replies)) {
                walk(record.replies);
            }
        }
    };
    walk(comments);
    return ids;
}

function feedNodeHasGuessScores(guesses: GuessCell[][] | undefined): boolean {
    return (guesses ?? []).some((row) => row && row.length > 0);
}

type FeedReply = {
    id: string;
    apiCommentId?: string | null;
    hasRealId?: boolean;
    username: string;
    profilePhoto?: string | null;
    time: string;
    text: string | null;
    guesses: GuessCell[][];
    isAgent?: boolean;
    isFollowing?: boolean;
    showScoreLeaderboard?: boolean;
    userId?: string;
    replies?: FeedReply[];
};

function DeleteConfirmModal({
    isOpen,
    onConfirm,
    onCancel,
    isDeleting = false,
}: {
    isOpen: boolean;
    onConfirm: () => void;
    onCancel: () => void;
    isDeleting?: boolean;
}) {
    const cancelButtonRef = useRef<HTMLButtonElement>(null);

    // Focus management - move focus to cancel button when modal opens
    useEffect(() => {
        if (isOpen && cancelButtonRef.current) {
            cancelButtonRef.current.focus();
        }
    }, [isOpen]);

    // Handle escape key to close modal
    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape" && !isDeleting) {
                onCancel();
            }
        };

        if (isOpen) {
            document.addEventListener("keydown", handleEscape);
            return () => document.removeEventListener("keydown", handleEscape);
        }
    }, [isOpen, isDeleting, onCancel]);

    if (!isOpen) return null;

    return (
        <div
            className="tw-fixed tw-inset-0 tw-bg-black/50 tw-backdrop-blur-sm tw-flex tw-items-center tw-justify-center tw-z-[1000] tw-px-4 tw-py-8"
            onClick={(e) => {
                if (e.target === e.currentTarget && !isDeleting) {
                    onCancel();
                }
            }}
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="delete-modal-title"
                aria-describedby="delete-modal-description"
                className="tw-bg-[#232340] tw-rounded-lg tw-p-4 sm:tw-p-6 tw-w-full tw-max-w-xs sm:tw-max-w-sm tw-border tw-border-gray-600 tw-mx-auto"
            >
                <h3
                    id="delete-modal-title"
                    className="tw-text-white tw-text-base sm:tw-text-lg tw-font-semibold tw-mb-2 sm:tw-mb-3"
                >
                    Delete Comment
                </h3>
                <p
                    id="delete-modal-description"
                    className="tw-text-gray-300 tw-text-sm tw-mb-4 sm:tw-mb-6 tw-leading-relaxed"
                >
                    Are you sure you want to delete this comment? This action cannot be undone.
                </p>
                <div className="tw-flex tw-flex-col sm:tw-flex-row tw-gap-2 sm:tw-gap-3 tw-justify-end">
                    <button
                        ref={cancelButtonRef}
                        onClick={onCancel}
                        disabled={isDeleting}
                        className="tw-px-3 sm:tw-px-4 tw-py-2 tw-text-sm tw-text-gray-300 tw-bg-transparent tw-border tw-border-gray-500 tw-rounded-lg hover:tw-bg-gray-700 tw-transition-colors tw-duration-200 tw-w-full sm:tw-w-auto disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={isDeleting}
                        className="tw-px-3 sm:tw-px-4 tw-py-2 tw-text-sm tw-text-white tw-bg-red-600 tw-border tw-border-red-600 tw-rounded-lg hover:tw-bg-red-700 tw-transition-colors tw-duration-200 tw-w-full sm:tw-w-auto disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                    >
                        {isDeleting ? "Deleting..." : "Delete"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function Avatar({
    nickname,
    profilePhoto,
    size = 36,
    isAgent = false,
    isUserAuthenticated = false,
    onRequireSignInForProfileView,
}: {
    nickname: string;
    profilePhoto?: string | null;
    size?: number;
    isAgent?: boolean;
    isUserAuthenticated?: boolean;
    onRequireSignInForProfileView?: () => void;
}) {
    const colors = [
        "tw-bg-violet-600",
        "tw-bg-orange-500",
        "tw-bg-green-500",
        "tw-bg-blue-500",
        "tw-bg-red-500",
        "tw-bg-yellow-500",
    ];
    const idx = nickname.charCodeAt(0) % colors.length;
    const sizeClass = size <= 28 ? "tw-w-7 tw-h-7 tw-text-xs" : "tw-w-9 tw-h-9 tw-text-sm";

    const handleClick = (e: React.MouseEvent) => {
        if (!isUserAuthenticated && onRequireSignInForProfileView) {
            e.preventDefault();
            onRequireSignInForProfileView();
            return;
        }
    };

    return (
        <div className="tw-relative tw-flex-shrink-0">
            {profilePhoto ? (
                <Link
                    to={`/profile/${encodeURIComponent(nickname.trim())}`}
                    onClick={handleClick}
                    aria-label={`View ${nickname}'s profile`}
                    title={`View ${nickname}'s profile`}
                    className={clsx(
                        "tw-flex tw-items-center tw-justify-center tw-rounded-full tw-transition-transform tw-duration-200 hover:tw-scale-105 tw-no-underline tw-overflow-hidden",
                        sizeClass,
                        colors[idx], // Background color fallback
                    )}
                >
                    <img
                        src={profilePhoto}
                        alt={`${nickname} profile`}
                        className="tw-w-full tw-h-full tw-object-cover"
                        onError={(e) => {
                            // Hide broken image, let background color show through
                            const target = e.target as HTMLImageElement;
                            target.style.display = "none";
                        }}
                    />
                    <span className="tw-sr-only">View {nickname}'s profile</span>
                    {/* Fallback text that shows when image fails to load */}
                    <span className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-text-white tw-font-bold tw-opacity-0 tw-pointer-events-none">
                        {nickname[0].toUpperCase()}
                    </span>
                </Link>
            ) : (
                <Link
                    to={`/profile/${encodeURIComponent(nickname.trim())}`}
                    onClick={handleClick}
                    aria-label={`View ${nickname}'s profile`}
                    title={`View ${nickname}'s profile`}
                    className={clsx(
                        "tw-flex tw-items-center tw-justify-center tw-text-white tw-rounded-full tw-font-bold tw-transition-transform tw-duration-200 hover:tw-scale-105 tw-no-underline",
                        sizeClass,
                        colors[idx],
                    )}
                >
                    {nickname[0].toUpperCase()}
                </Link>
            )}
            {isAgent && null}
        </div>
    );
}

function FeedEntry({
    id,
    apiCommentId,
    hasRealId,
    username,
    profilePhoto,
    time,
    text,
    guesses,
    isAgent = false,
    isFollowing: initialIsFollowing,
    showScoreLeaderboard = true,
    userId,
    actualPrice,
    replies = [],
    depth = 0,
    replyingTo,
    onReply,
    onCancelReply,
    expandedGrids,
    onToggleGrid,
    puzzleCompleted,
    currentUserNickname,
    currentUserId,
    onDeleteRequest,
    replyDraft,
    onReplyDraftChange,
    onSubmitReply,
    isReplySubmitting,
    isUserAuthenticated = false,
    onRequireSignInForProfileView,
    onRequireSignInForFollow,
    onRequireSignInForComment,
    onRequireSignInForCommentLike,
    onFollowUpdate,
    commentLikeStateById,
    playablePropertyId,
    replyExpansionCommentIds,
}: {
    id: string;
    apiCommentId?: string | null;
    hasRealId?: boolean;
    username: string;
    profilePhoto?: string | null;
    time: string;
    text: string | null;
    guesses: GuessCell[][];
    isAgent?: boolean;
    isFollowing?: boolean;
    showScoreLeaderboard?: boolean;
    userId?: string;
    actualPrice?: number;
    replies: FeedReply[];
    depth?: number;
    replyingTo: string | null;
    onReply: (id: string) => void;
    onCancelReply: () => void;
    expandedGrids: Record<string, boolean>;
    onToggleGrid: (id: string) => void;
    puzzleCompleted: boolean;
    currentUserNickname?: string | null;
    currentUserId?: string | null;
    onDeleteRequest: (id: string) => void;
    replyDraft: string;
    onReplyDraftChange: (value: string) => void;
    onSubmitReply: (stableId: string) => void;
    isReplySubmitting: boolean;
    isUserAuthenticated?: boolean;
    onRequireSignInForProfileView?: () => void;
    onRequireSignInForFollow?: () => void;
    onRequireSignInForComment?: () => void;
    onRequireSignInForCommentLike?: () => void;
    onFollowUpdate?: (followedUserId?: string, nextIsFollowing?: boolean) => void;
    commentLikeStateById: Map<string, { count: number; likedByCurrentUser: boolean }>;
    playablePropertyId?: string;
    replyExpansionCommentIds?: Set<string>;
}) {
    const { commentLikeCount, commentLikedByCurrentUser } = resolveCommentLikeState(
        apiCommentId,
        commentLikeStateById,
    );
    const [showAllReplies, setShowAllReplies] = useState(false);
    const forceShowAllReplies = Boolean(apiCommentId && replyExpansionCommentIds?.has(String(apiCommentId)));
    useEffect(() => {
        if (forceShowAllReplies) {
            setShowAllReplies(true);
        }
    }, [forceShowAllReplies]);
    const hasGuesses = guesses.length > 0;
    const isScoreOnly = !text && hasGuesses;
    const hasTextAndScore = !!text && hasGuesses;
    const effectiveShowAllReplies = showAllReplies || forceShowAllReplies;
    const visibleReplies = effectiveShowAllReplies ? replies : replies.slice(0, PUZZLE_VISIBLE_REPLY_LIMIT);
    const hiddenCount = replies.length - PUZZLE_VISIBLE_REPLY_LIMIT;
    const gridExpanded = expandedGrids[id] || false;
    const { isFollowing, isPending: isFollowPending, toggleFollow } = useAgentFollow({
        followerUserId: currentUserId ?? null,
        followedUserId: userId ?? null,
        initialIsFollowing,
        isAuthenticated: isUserAuthenticated,
        onRequireAuth: () => onRequireSignInForFollow?.(),
        mixpanelLocation: "Scoreboard",
        nicknameFollowed: username,
        onToggleSuccess: (action) => {
            onFollowUpdate?.(userId, action === "follow");
        },
    });
    const resolvedIsFollowing =
        isFollowPending || typeof initialIsFollowing !== "boolean" ? isFollowing : initialIsFollowing;

    // Check if this is the current user's own comment/score
    const isOwnComment = userId && currentUserId && userId === currentUserId;

    // Determine if score should be visible: user allows OR it's their own comment
    const shouldShowScore = showScoreLeaderboard || isOwnComment;

    // Determine if user is eligible to see real score data (not just blurred)
    const canSeeScoreData = (puzzleCompleted && isUserAuthenticated) || isOwnComment;

    // Determine if score should be blurred: authenticated but puzzle not completed (and not own comment)
    const shouldBlurScore = isUserAuthenticated && !puzzleCompleted && !isOwnComment;

    // Check if user solved the puzzle by comparing last guess with actual price
    const isSolved =
        hasGuesses && actualPrice ? guesses[guesses.length - 1]?.every((cell) => cell.status === "correct") : false;

    const handleUsernameClick = (e: React.MouseEvent) => {
        if (!isUserAuthenticated && onRequireSignInForProfileView) {
            e.preventDefault();
            onRequireSignInForProfileView();
            return;
        }
    };

    const handleDeleteClick = () => {
        onDeleteRequest(id);
    };

    const handleFollowClick = () => {
        toggleFollow();
    };

    return (
        <div
            id={apiCommentId ? `puzzle-comment-${apiCommentId}` : undefined}
            className={clsx(depth > 0 && "tw-ml-5")}
        >
            <div className={clsx("tw-flex tw-gap-2.5", depth > 0 ? "tw-py-2" : "tw-py-3")}>
                <Avatar
                    nickname={username}
                    profilePhoto={profilePhoto}
                    size={depth > 0 ? 28 : 36}
                    isAgent={isAgent}
                    isUserAuthenticated={isUserAuthenticated}
                    onRequireSignInForProfileView={onRequireSignInForProfileView}
                />
                <div className="tw-flex-1 tw-min-w-0">
                    {/* Username row */}
                    <div className="tw-flex tw-items-center tw-gap-1.5 tw-mb-1">
                        <Link
                            to={`/profile/${encodeURIComponent(username.trim())}`}
                            onClick={handleUsernameClick}
                            className={clsx(
                                "tw-font-semibold tw-text-white tw-transition-colors tw-duration-200 hover:tw-text-violet-400 tw-no-underline",
                                depth > 0 ? "tw-text-xs" : "tw-text-sm",
                            )}
                        >
                            {username}
                        </Link>
                        {isAgent && !!userId && !isOwnComment && (
                            <button
                                type="button"
                                onClick={handleFollowClick}
                                disabled={isFollowPending}
                                className={clsx(
                                    "tw-px-2.5 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-semibold tw-whitespace-nowrap tw-leading-tight tw-transition-all tw-duration-200",
                                    resolvedIsFollowing
                                        ? "tw-bg-transparent tw-border tw-border-gray-500 tw-text-gray-400"
                                        : "tw-bg-orange-500 tw-border tw-border-transparent tw-text-white",
                                )}
                            >
                                {resolvedIsFollowing ? "Following" : "Follow"}
                            </button>
                        )}
                        <div className="tw-ml-auto tw-flex tw-items-center tw-gap-2.5 tw-shrink-0">
                            <span className="tw-text-xs tw-text-gray-500">{time}</span>
                            {playablePropertyId && apiCommentId && (
                                <CommentLikeControl
                                    commentId={String(apiCommentId)}
                                    playablePropertyId={playablePropertyId}
                                    authorNickname={username}
                                    initialLikeCount={commentLikeCount}
                                    initialLikedByCurrentUser={commentLikedByCurrentUser}
                                    userId={currentUserId ?? null}
                                    isUserAuthenticated={isUserAuthenticated}
                                    onRequireAuth={() => onRequireSignInForCommentLike?.()}
                                />
                            )}
                        </div>
                    </div>

                    {/* Score badge below username for text+score entries */}
                    {hasTextAndScore && (
                        <div className="tw-mb-1.5">
                            {shouldShowScore ? (
                                canSeeScoreData ? (
                                    <div className={clsx(shouldBlurScore && "tw-blur-sm tw-pointer-events-none")} aria-hidden={shouldBlurScore}>
                                        <ScoreBadge
                                            guesses={guesses}
                                            expanded={shouldBlurScore ? false : gridExpanded}
                                            onToggle={shouldBlurScore ? () => {} : () => onToggleGrid(id)}
                                            compact={depth > 0}
                                            isSolved={isSolved}
                                        />
                                    </div>
                                ) : (
                                    <div className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-1 tw-rounded-md tw-bg-violet-600/8 tw-border tw-border-violet-600/20">
                                        <div className="tw-flex tw-flex-col tw-gap-0.5 tw-blur-sm">
                                            {Array.from({ length: 2 }, (_, i) => (
                                                <div key={i} className="tw-flex tw-gap-0.5">
                                                    {Array.from({ length: 5 }, (_, j) => (
                                                        <div
                                                            key={j}
                                                            className="tw-w-1 tw-h-1 tw-rounded-sm tw-bg-violet-600"
                                                        />
                                                    ))}
                                                </div>
                                            ))}
                                        </div>
                                        <span className="tw-text-xs tw-text-gray-500">
                                            {!isUserAuthenticated ? "Sign in to view scores" : "Complete puzzle to view scores"}
                                        </span>
                                    </div>
                                )
                            ) : (
                                <div className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-1 tw-rounded-md tw-bg-violet-600/8 tw-border tw-border-violet-600/20">
                                    <div className="tw-flex tw-flex-col tw-gap-0.5 tw-blur-sm">
                                        {Array.from({ length: 2 }, (_, i) => (
                                            <div key={i} className="tw-flex tw-gap-0.5">
                                                {Array.from({ length: 5 }, (_, j) => (
                                                    <div
                                                        key={j}
                                                        className="tw-w-1 tw-h-1 tw-rounded-sm tw-bg-violet-600"
                                                    />
                                                ))}
                                            </div>
                                        ))}
                                    </div>
                                    <span className="tw-text-xs tw-text-gray-500">Score hidden by user</span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Score-only: show full grid */}
                    {isScoreOnly && (
                        <div className="tw-mb-0.5">
                            {shouldShowScore ? (
                                canSeeScoreData ? (
                                    shouldBlurScore ? (
                                        <div className="tw-blur-sm tw-pointer-events-none" aria-hidden={true}>
                                            <div className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-1 tw-rounded-md tw-bg-violet-600/8 tw-border tw-border-violet-600/20">
                                                <div className="tw-flex tw-flex-col tw-gap-0.5">
                                                    {Array.from({ length: 3 }, (_, rowIndex) => (
                                                        <div key={rowIndex} className="tw-flex tw-gap-0.5">
                                                            {Array.from({ length: 5 }, (_, colIndex) => (
                                                                <div
                                                                    key={colIndex}
                                                                    className="tw-w-6 tw-h-6 tw-rounded tw-bg-gray-600 tw-flex tw-items-center tw-justify-center tw-text-xs tw-text-white"
                                                                >
                                                                    ?
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <GuessGrid guesses={guesses} compact={depth > 0} />
                                    )
                                ) : (
                                    <div className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-1 tw-rounded-md tw-bg-violet-600/8 tw-border tw-border-violet-600/20">
                                        <div className="tw-flex tw-flex-col tw-gap-0.5" aria-hidden="true">
                                            {Array.from({ length: 3 }, (_, rowIndex) => (
                                                <div key={rowIndex} className="tw-flex tw-gap-0.5">
                                                    {Array.from({ length: 5 }, (_, colIndex) => (
                                                        <div
                                                            key={colIndex}
                                                            className="tw-w-1 tw-h-1 tw-rounded-sm tw-bg-violet-600"
                                                        />
                                                    ))}
                                                </div>
                                            ))}
                                        </div>
                                        <span className="tw-text-xs tw-text-gray-500">
                                            {!isUserAuthenticated ? "Sign in to view scores" : "Complete puzzle to view scores"}
                                        </span>
                                    </div>
                                )
                            ) : (
                                <div className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-1 tw-rounded-md tw-bg-violet-600/8 tw-border tw-border-violet-600/20">
                                    <div className="tw-flex tw-flex-col tw-gap-0.5" aria-hidden="true">
                                        {Array.from({ length: 3 }, (_, rowIndex) => (
                                            <div key={rowIndex} className="tw-flex tw-gap-0.5">
                                                {Array.from({ length: 5 }, (_, colIndex) => (
                                                    <div
                                                        key={colIndex}
                                                        className="tw-w-1 tw-h-1 tw-rounded-sm tw-bg-violet-600"
                                                    />
                                                ))}
                                            </div>
                                        ))}
                                    </div>
                                    <span className="tw-text-xs tw-text-gray-500">Score hidden by user</span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Text */}
                    {text && (
                        <p
                            className={clsx(
                                "tw-text-white tw-m-0 tw-leading-relaxed",
                                depth > 0 ? "tw-text-sm" : "tw-text-base",
                                // Style deleted comments differently
                                text === "[deleted]" && "tw-text-gray-500 tw-italic",
                            )}
                        >
                            {text}
                        </p>
                    )}

                    {/* Actions — Reply on 3rd level (depth 2) posts under the parent comment so the thread never gains a 4th level */}
                    {depth <= 2 && apiCommentId && (
                        <div className="tw-flex tw-gap-4 tw-mt-1.5">
                            <button
                                type="button"
                                disabled={isReplySubmitting}
                                onClick={() => onReply(id)}
                                className="tw-bg-transparent tw-border-none tw-text-gray-400 tw-text-xs tw-cursor-pointer tw-p-0 tw-font-medium tw-flex tw-items-center tw-gap-1 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                            >
                                <span className="tw-text-sm">💬</span>
                                {depth === 0 && replies.length > 0 ? `${replies.length}` : "Reply"}
                            </button>
                            {/* Only show delete button for entries with text content (not score-only) */}
                            {isUserAuthenticated &&
                                currentUserNickname &&
                                currentUserNickname === username &&
                                hasRealId &&
                                text &&
                                text !== "[deleted]" && ( // Only show delete for entries with non-deleted text content
                                    <button
                                        onClick={handleDeleteClick}
                                        className="tw-bg-transparent tw-border-none tw-text-red-400 tw-text-xs tw-cursor-pointer tw-p-0 tw-font-medium tw-flex tw-items-center tw-gap-1 hover:tw-text-red-300 tw-transition-colors tw-duration-200"
                                    >
                                        Delete
                                    </button>
                                )}
                        </div>
                    )}
                </div>
            </div>

            {/* Replies */}
            {depth < 2 &&
                visibleReplies.map((reply) => (
                    <FeedEntry
                        key={reply.id}
                        {...reply}
                        actualPrice={actualPrice}
                        replies={depth < 1 ? reply.replies || [] : []}
                        depth={depth + 1}
                        replyingTo={replyingTo}
                        onReply={onReply}
                        onCancelReply={onCancelReply}
                        expandedGrids={expandedGrids}
                        onToggleGrid={onToggleGrid}
                        puzzleCompleted={puzzleCompleted}
                        currentUserNickname={currentUserNickname}
                        currentUserId={currentUserId}
                        onDeleteRequest={onDeleteRequest}
                        replyDraft={replyDraft}
                        onReplyDraftChange={onReplyDraftChange}
                        onSubmitReply={onSubmitReply}
                        isReplySubmitting={isReplySubmitting}
                        isUserAuthenticated={isUserAuthenticated}
                        onRequireSignInForProfileView={onRequireSignInForProfileView}
                        onRequireSignInForFollow={onRequireSignInForFollow}
                        onRequireSignInForComment={onRequireSignInForComment}
                        onRequireSignInForCommentLike={onRequireSignInForCommentLike}
                        playablePropertyId={playablePropertyId}
                        commentLikeStateById={commentLikeStateById}
                        onFollowUpdate={onFollowUpdate}
                        replyExpansionCommentIds={replyExpansionCommentIds}
                    />
                ))}

            {!effectiveShowAllReplies && hiddenCount > 0 && (
                <button
                    onClick={() => setShowAllReplies(true)}
                    className="tw-bg-transparent tw-border-none tw-text-violet-400 tw-text-xs tw-cursor-pointer tw-font-medium tw-pl-10 tw-py-1"
                >
                    Show {hiddenCount} more {hiddenCount === 1 ? "reply" : "replies"}
                </button>
            )}

            {/* Inline reply input */}
            {replyingTo === id && depth <= 2 && apiCommentId && (
                <div
                    className={clsx("tw-mt-1 tw-mb-2", depth === 0 ? "tw-ml-12" : depth === 1 ? "tw-ml-8" : "tw-ml-6")}
                >
                    <textarea
                        autoFocus
                        value={replyDraft}
                        onChange={(e) => onReplyDraftChange(e.target.value.slice(0, MAX_REPLY_LENGTH))}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                if (isReplySubmitting) return;
                                onSubmitReply(id);
                            }
                        }}
                        placeholder={`Reply to ${username}...`}
                        rows={2}
                        disabled={isReplySubmitting}
                        className="tw-w-full tw-bg-gray-800 tw-border tw-border-violet-600 tw-rounded-lg tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-resize-none tw-outline-none tw-box-border disabled:tw-opacity-60 disabled:tw-cursor-not-allowed"
                    />
                    <div className="tw-flex tw-justify-end tw-gap-2 tw-mt-1.5">
                        <button
                            type="button"
                            disabled={isReplySubmitting}
                            onClick={onCancelReply}
                            className="tw-bg-transparent tw-border-none tw-text-gray-400 tw-text-xs tw-cursor-pointer tw-px-2.5 tw-py-1.5 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            disabled={isReplySubmitting}
                            onClick={() => onSubmitReply(id)}
                            className="tw-bg-gradient-to-r tw-from-orange-500 tw-to-orange-400 tw-border-none tw-rounded-lg tw-px-3.5 tw-py-1.5 tw-text-white tw-text-xs tw-font-semibold tw-cursor-pointer disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                        >
                            {isReplySubmitting ? "Posting..." : "Reply"}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export function CommentsSection({
    puzzleCompleted,
    comments = [],
    totalGuesses,
    totalReplies,
    actualPrice,
    convertStatsToGuessCell,
    isLoading = false,
    currentUserId,
    currentUserNickname,
    isUserAuthenticated = false,
    onRequireSignInForProfileView,
    onRequireSignInForFollow,
    onRequireSignInForComment,
    onRequireSignInForCommentLike,
    playablePropertyId,
    userScoreRow,
    onSaveGuestScore,
    onSubmitReplyApi,
    onCommentsUpdate,
    onFollowUpdate,
    scoreboardScrollContainerRef,
}: {
    puzzleCompleted: boolean;
    comments?: any[];
    totalGuesses?: number;
    totalReplies?: number;
    actualPrice?: number;
    convertStatsToGuessCell?: (guesses: number[], actualPrice?: number) => any[];
    isLoading?: boolean;
    currentUserId?: string | null;
    currentUserNickname?: string | null;
    isUserAuthenticated?: boolean;
    onRequireSignInForProfileView?: () => void;
    onRequireSignInForFollow?: () => void;
    onRequireSignInForComment?: () => void;
    onRequireSignInForCommentLike?: () => void;
    playablePropertyId?: string;
    userScoreRow?: UserScoreRowData | null;
    onSaveGuestScore?: () => void;
    onSubmitReplyApi?: (parentCommentId: string, content: string) => Promise<void>;
    onCommentsUpdate?: () => void;
    onFollowUpdate?: (followedUserId?: string, nextIsFollowing?: boolean) => void;
    scoreboardScrollContainerRef?: RefObject<HTMLElement | null>;
}) {
    const { sendEvent: sendMixpanelEvent } = useMixpanelEvent();
    const { userState } = useUser();
    const location = useLocation();
    const navigate = useNavigate();
    const [pendingFocusCommentId, setPendingFocusCommentId] = useState<string | null>(null);
    const [replyingTo, setReplyingTo] = useState<string | null>(null);
    const [replyDraft, setReplyDraft] = useState("");
    const [isReplySubmitting, setIsReplySubmitting] = useState(false);
    /** Prevents duplicate submits before React re-renders after the first Enter. */
    const replySubmitInFlightRef = useRef(false);
    const [expandedGrids, setExpandedGrids] = useState<Record<string, boolean>>({});

    // Delete modal state - lifted to this level to avoid multiple modal instances
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [deletingStableId, setDeletingStableId] = useState<string | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const handleFollowUpdate = (followedUserId?: string, nextIsFollowing?: boolean) => {
        onFollowUpdate?.(followedUserId, nextIsFollowing);
    };

    const commentIds = useMemo(() => collectApiCommentIds(comments), [comments]);

    const stripFocusCommentIdFromUrl = useCallback((): void => {
        const strippedSearch = stripPuzzleCommentIdFromSearch(location.search);
        const nextUrl = `${location.pathname}${strippedSearch}${location.hash}`;
        const currentUrl = `${location.pathname}${location.search}${location.hash}`;
        if (currentUrl !== nextUrl) {
            navigate(nextUrl, { replace: true });
        }
    }, [location.hash, location.pathname, location.search, navigate]);

    const clearFocusCommentIntent = useCallback((): void => {
        try {
            sessionStorage.removeItem(PUZZLE_FOCUS_COMMENT_ID_KEY);
            sessionStorage.removeItem(PUZZLE_FOCUS_COMMENT_PATH_KEY);
        } catch {
            /* ignore */
        }
        setPendingFocusCommentId(null);
        stripFocusCommentIdFromUrl();
    }, [stripFocusCommentIdFromUrl]);

    useLayoutEffect(() => {
        const focusCommentIdFromUrl = readPuzzleFocusCommentIdFromSearch(location.search);
        if (focusCommentIdFromUrl) {
            try {
                sessionStorage.setItem(PUZZLE_FOCUS_COMMENT_ID_KEY, focusCommentIdFromUrl);
                sessionStorage.setItem(PUZZLE_FOCUS_COMMENT_PATH_KEY, location.pathname);
            } catch {
                /* ignore */
            }
            setPendingFocusCommentId(focusCommentIdFromUrl);
            return;
        }
        let focusCommentIdFromStorage: string | null = null;
        let focusCommentPathFromStorage: string | null = null;
        try {
            focusCommentIdFromStorage = sessionStorage.getItem(PUZZLE_FOCUS_COMMENT_ID_KEY);
            focusCommentPathFromStorage = sessionStorage.getItem(PUZZLE_FOCUS_COMMENT_PATH_KEY);
        } catch {
            focusCommentIdFromStorage = null;
            focusCommentPathFromStorage = null;
        }
        if (!focusCommentIdFromStorage || focusCommentPathFromStorage !== location.pathname) {
            if (focusCommentIdFromStorage) {
                clearFocusCommentIntent();
            }
            return;
        }
        setPendingFocusCommentId(focusCommentIdFromStorage);
    }, [clearFocusCommentIntent, location.pathname, location.search]);

    const replyExpansionCommentIds = useMemo(() => {
        if (!pendingFocusCommentId || comments.length === 0) {
            return new Set<string>();
        }
        return findCommentIdsRequiringReplyExpansion(pendingFocusCommentId, comments);
    }, [comments, pendingFocusCommentId]);
    const replyExpansionKey = useMemo(() => [...replyExpansionCommentIds].sort().join(","), [replyExpansionCommentIds]);
    const requestingUserKey = currentUserId ? String(currentUserId) : "anonymous";
    const { data: commentLikesData } = useQuery({
        queryKey: [
            "comment-like-counts",
            playablePropertyId ?? "",
            currentUserId,
            requestingUserKey,
            commentIds.join(","),
        ],
        queryFn: () =>
            getLikeCounts({
                items: commentIds.map((commentId) => ({
                    targetType: COMMENT_LIKE_TARGET_TYPE,
                    targetId: commentId,
                })),
                userId: currentUserId ?? undefined,
            }),
        enabled: Boolean(playablePropertyId) && commentIds.length > 0,
        retry: false,
    });
    const commentLikeStateById = useMemo(() => {
        const map = new Map<string, { count: number; likedByCurrentUser: boolean }>();
        commentLikesData?.counts?.forEach((entry) => {
            map.set(String(entry.targetId), {
                count: entry.count,
                likedByCurrentUser: entry.likedByCurrentUser,
            });
        });
        return map;
    }, [commentLikesData?.counts]);

    useEffect(() => {
        if (!pendingFocusCommentId || comments.length === 0) {
            return;
        }
        let attempt = 0;
        let cancelled = false;
        let retryTimer: number | undefined;
        const scrollToFocusedComment = (): void => {
            if (cancelled) {
                return;
            }
            const element = document.getElementById(`puzzle-comment-${pendingFocusCommentId}`);
            const scrollContainer = resolveScoreboardScrollContainer(scoreboardScrollContainerRef);
            if (!element || !scrollContainer) {
                attempt += 1;
                if (attempt < FOCUS_COMMENT_SCROLL_MAX_ATTEMPTS) {
                    retryTimer = window.setTimeout(scrollToFocusedComment, FOCUS_COMMENT_SCROLL_RETRY_MS);
                    return;
                }
                clearFocusCommentIntent();
                return;
            }
            scrollElementWithinContainer(element, scrollContainer, { behavior: "smooth" });
            clearFocusCommentIntent();
        };
        const scrollTimer = window.setTimeout(scrollToFocusedComment, 0);
        return () => {
            cancelled = true;
            window.clearTimeout(scrollTimer);
            if (retryTimer) {
                window.clearTimeout(retryTimer);
            }
        };
    }, [
        clearFocusCommentIntent,
        comments,
        pendingFocusCommentId,
        replyExpansionKey,
        scoreboardScrollContainerRef,
    ]);

    // Process comments data from the new API response structure
    const feedData = comments.map((comment) => {
        // Handle null id for guess-only users - use deterministic fallback to userId
        const stableId = comment.id || `guess-${comment.userId}`;
        const hasRealId = !!comment.id;

        // Handle null createdAt for guess-only users
        const hasValidTimestamp = comment.createdAt && comment.createdAt !== null;
        const formattedTime = hasValidTimestamp ? moment(comment.createdAt).fromNow() : "Recently";

        return {
            id: stableId,
            apiCommentId: comment.id,
            hasRealId,
            username: comment.username,
            profilePhoto: comment.profilePhoto,
            time: formattedTime,
            text: comment.body,
            guesses: convertStatsToGuessCell ? convertStatsToGuessCell(comment.guesses, actualPrice) : [],
            isAgent: Boolean(comment.isAgent),
            isFollowing: typeof comment.isFollowing === "boolean" ? comment.isFollowing : undefined,
            showScoreLeaderboard: comment.showScoreLeaderboard ?? true, // Default to true if not provided
            userId: comment.userId, // Include userId for comparison with current user
            replies:
                comment.replies?.map((reply: any) => {
                    const replyId = reply.id || `reply-${reply.userId}`;
                    const replyHasRealId = !!reply.id;
                    const replyHasValidTimestamp = reply.createdAt && reply.createdAt !== null;
                    const replyFormattedTime = replyHasValidTimestamp ? moment(reply.createdAt).fromNow() : "Recently";

                    return {
                        id: replyId,
                        apiCommentId: reply.id,
                        hasRealId: replyHasRealId,
                        username: reply.username,
                        profilePhoto: reply.profilePhoto,
                        time: replyFormattedTime,
                        text: reply.body,
                        guesses: convertStatsToGuessCell
                            ? convertStatsToGuessCell(reply.guesses || [], actualPrice)
                            : [],
                        isAgent: Boolean(reply.isAgent),
                        isFollowing: typeof reply.isFollowing === "boolean" ? reply.isFollowing : undefined,
                        showScoreLeaderboard: reply.showScoreLeaderboard ?? true, // Default to true if not provided
                        userId: reply.userId, // Include userId for comparison with current user
                        replies:
                            reply.replies?.map((nestedReply: any) => {
                                const nestedReplyId = nestedReply.id || `nested-${nestedReply.userId}`;
                                const nestedReplyHasRealId = !!nestedReply.id;
                                const nestedReplyHasValidTimestamp =
                                    nestedReply.createdAt && nestedReply.createdAt !== null;
                                const nestedReplyFormattedTime = nestedReplyHasValidTimestamp
                                    ? moment(nestedReply.createdAt).fromNow()
                                    : "Recently";

                                return {
                                    id: nestedReplyId,
                                    apiCommentId: nestedReply.id,
                                    hasRealId: nestedReplyHasRealId,
                                    username: nestedReply.username,
                                    profilePhoto: nestedReply.profilePhoto,
                                    time: nestedReplyFormattedTime,
                                    text: nestedReply.body,
                                    guesses: convertStatsToGuessCell
                                        ? convertStatsToGuessCell(nestedReply.guesses || [], actualPrice)
                                        : [],
                                    isAgent: Boolean(nestedReply.isAgent),
                                    isFollowing:
                                        typeof nestedReply.isFollowing === "boolean"
                                            ? nestedReply.isFollowing
                                            : undefined,
                                    showScoreLeaderboard: nestedReply.showScoreLeaderboard ?? true, // Default to true if not provided
                                    userId: nestedReply.userId, // Include userId for comparison with current user
                                    replies: [], // Max depth reached
                                };
                            }) || [],
                    };
                }) || [],
        };
    });

    type FeedNode = (typeof feedData)[number];

    const stableIdToMeta = useMemo(() => {
        const m = new Map<
            string,
            {
                apiCommentId: string | null;
                text: string | null;
                hasGuessScores: boolean;
                /** Parent id for POST when replying; at max UI depth this is the grandparent so we never create a 4th level. */
                apiParentIdForReply: string | null;
            }
        >();
        const walk = (node: FeedNode | FeedReply, depth: number, parentApiCommentId: string | null) => {
            const apiParentIdForReply = depth >= 2 ? parentApiCommentId : (node.apiCommentId ?? null);
            m.set(node.id, {
                apiCommentId: node.apiCommentId ?? null,
                text: node.text,
                hasGuessScores: feedNodeHasGuessScores(node.guesses),
                apiParentIdForReply,
            });
            const nextParentApi = node.apiCommentId ?? parentApiCommentId;
            (node.replies || []).forEach((child) => walk(child, depth + 1, nextParentApi));
        };
        feedData.forEach((n) => walk(n, 0, null));
        return m;
    }, [feedData]);

    const handleReply = (id: string) => {
        if (isReplySubmitting) return;
        if (replyingTo === id) {
            setReplyingTo(null);
            setReplyDraft("");
        } else {
            setReplyingTo(id);
            setReplyDraft("");
        }
    };

    const handleSubmitReply = async (stableId: string) => {
        if (!onSubmitReplyApi) return;
        // Ref must be claimed synchronously before any await so Enter + click can’t both pass.
        if (replySubmitInFlightRef.current || isReplySubmitting) return;
        replySubmitInFlightRef.current = true;
        try {
            if (!isUserAuthenticated) {
                onRequireSignInForComment?.();
                return;
            }
            const userId = (userState as any)?.id;
            if (!userId) {
                toast.error("We are still loading your profile. Please try again in a moment.");
                return;
            }
            const trimmed = replyDraft.trim();
            if (!trimmed) {
                toast.error("Please enter a reply");
                return;
            }

            const meta = stableIdToMeta.get(stableId);
            if (!meta?.apiCommentId || !meta.apiParentIdForReply) {
                toast.error("You can't reply to this thread.");
                return;
            }

            setIsReplySubmitting(true);
            try {
                await onSubmitReplyApi(meta.apiParentIdForReply, trimmed);
                sendMixpanelEvent("Comment Posted", {
                    pageUrl: window.location.href,
                    email: (userState as any)?.emailAddress ?? null,
                    nickname: (userState as any)?.nickname ?? null,
                    playable_property_id: playablePropertyId ?? null,
                    comment_type: meta.hasGuessScores ? "score_reply" : "comment_reply",
                    character_count: trimmed.length,
                    comment_text: trimmed,
                });
                setReplyDraft("");
                setReplyingTo(null);
            } catch (error: any) {
                console.error(error);
                toast.error(error?.response?.data?.message || "Failed to post reply");
            } finally {
                setIsReplySubmitting(false);
            }
        } finally {
            replySubmitInFlightRef.current = false;
        }
    };

    const handleToggleGrid = (id: string) => setExpandedGrids((prev) => ({ ...prev, [id]: !prev[id] }));

    // Delete modal handlers - lifted to this level
    const handleDeleteRequest = (stableId: string) => {
        if (isDeleting) return;
        setDeletingStableId(stableId);
        setDeleteModalOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!deletingStableId || isDeleting) return;
        setIsDeleting(true);
        try {
            const success = await handleDeleteComment(deletingStableId);
            if (success) {
                setDeleteModalOpen(false);
                setDeletingStableId(null);
            }
            // If deletion failed, keep modal open so user can retry
        } finally {
            setIsDeleting(false);
        }
    };

    const handleDeleteCancel = () => {
        if (isDeleting) return;
        setDeleteModalOpen(false);
        setDeletingStableId(null);
    };

    const handleDeleteComment = async (stableId: string): Promise<boolean> => {
        if (!playablePropertyId || !currentUserId) {
            toast.error("Unable to delete comment. Please try again.");
            return false;
        }

        // Use existing stableIdToMeta mapping instead of traversing comments again
        const meta = stableIdToMeta.get(stableId);
        if (!meta?.apiCommentId) {
            toast.error("Cannot delete this comment.");
            return false;
        }

        // Find the comment in feedData to check deletion conditions
        const findCommentInFeedData = (data: typeof feedData, targetId: string): any => {
            for (const entry of data) {
                if (entry.id === targetId) return entry;
                if (entry.replies) {
                    for (const reply of entry.replies) {
                        if (reply.id === targetId) return reply;
                        if (reply.replies) {
                            for (const nestedReply of reply.replies) {
                                if (nestedReply.id === targetId) return nestedReply;
                            }
                        }
                    }
                }
            }
            return null;
        };

        const commentToDelete = findCommentInFeedData(feedData, stableId);
        if (!commentToDelete) {
            toast.error("Comment not found.");
            return false;
        }

        const hasReplies = commentToDelete.replies && commentToDelete.replies.length > 0;
        const hasText = !!commentToDelete.text && commentToDelete.text !== "[deleted]";

        // Determine deletion behavior based on comment type:
        // 1. Text+Score entry: soft delete (replace text with "[deleted]", preserve score and structure)
        // 2. Score-only entry: not deletable (no delete button should appear)
        // 3. Text-only with replies: soft delete (replace text with "[deleted]", preserve replies)
        // 4. Text-only without replies: hard delete (remove entirely)

        if (!hasText) {
            // This shouldn't happen since delete button only shows for text entries
            toast.error("Cannot delete this comment.");
            return false;
        }

        const shouldSoftDelete = hasReplies;

        try {
            await deletePlayablePropertyComment(playablePropertyId, meta.apiCommentId, currentUserId);

            sendMixpanelEvent("Comment Deleted", {
                pageUrl: window.location.href,
                email: (userState as any)?.emailAddress ?? null,
                nickname: (userState as any)?.nickname ?? null,
                comment_text: meta.text,
            });

            if (shouldSoftDelete) {
                toast.success("Comment deleted. Replies preserved.");
            } else {
                toast.success("Comment deleted successfully");
            }

            // Refresh comments after successful deletion
            if (onCommentsUpdate) {
                onCommentsUpdate();
            }

            return true;
        } catch (error) {
            console.error("Delete comment error:", error);
            toast.error("Failed to delete comment. Please try again.");
            return false;
        }
    };

    const totalComments =
        totalReplies ??
        feedData.reduce((sum, e) => sum + (e.text ? 1 : 0) + (e.replies?.filter((r: any) => r.text).length || 0), 0);

    return (
        <div className="tw-bg-[#1a1a2e] tw-text-white tw-relative tw-min-h-full">
            {userScoreRow ? (
                <div className="tw-pt-3">
                    <UserScoreRow data={userScoreRow} onSave={onSaveGuestScore ?? (() => undefined)} />
                </div>
            ) : null}

            {/* Sort */}
            <div className="tw-flex tw-justify-between tw-items-center tw-px-4 tw-py-2 tw-border-t tw-border-[#3a3a5c] tw-mt-1 tw-pt-3">
                <span className="tw-text-sm tw-font-semibold tw-text-gray-400">
                    {totalGuesses ?? feedData.length} scores &middot; {totalComments} comments
                </span>
            </div>

            {/* Feed */}
            <div className="tw-px-4 tw-pb-5">
                {feedData.map((entry) => (
                    <div key={entry.id} className="tw-border-b tw-border-[#3a3a5c]">
                        <FeedEntry
                            {...entry}
                            actualPrice={actualPrice}
                            replies={entry.replies || []}
                            depth={0}
                            replyingTo={replyingTo}
                            onReply={handleReply}
                            onCancelReply={() => {
                                setReplyingTo(null);
                                setReplyDraft("");
                            }}
                            expandedGrids={expandedGrids}
                            onToggleGrid={handleToggleGrid}
                            puzzleCompleted={puzzleCompleted}
                            currentUserNickname={currentUserNickname}
                            currentUserId={currentUserId}
                            onDeleteRequest={handleDeleteRequest}
                            replyDraft={replyDraft}
                            onReplyDraftChange={setReplyDraft}
                            onSubmitReply={handleSubmitReply}
                            isReplySubmitting={isReplySubmitting}
                            isUserAuthenticated={isUserAuthenticated}
                            onRequireSignInForProfileView={onRequireSignInForProfileView}
                            onRequireSignInForFollow={onRequireSignInForFollow}
                            onRequireSignInForComment={onRequireSignInForComment}
                            onRequireSignInForCommentLike={onRequireSignInForCommentLike}
                            playablePropertyId={playablePropertyId}
                            commentLikeStateById={commentLikeStateById}
                            onFollowUpdate={handleFollowUpdate}
                            replyExpansionCommentIds={replyExpansionCommentIds}
                        />
                    </div>
                ))}

                {/* No Load More Button - all comments loaded at once */}
            </div>

            {/* Single Delete Confirmation Modal - lifted to this level */}
            <DeleteConfirmModal
                isOpen={deleteModalOpen}
                onConfirm={handleDeleteConfirm}
                onCancel={handleDeleteCancel}
                isDeleting={isDeleting}
            />
        </div>
    );
}
